import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { Badge } from '@rudi/ui/badge';
import { Input } from '@rudi/ui/input';
import { Button } from '@rudi/ui/button';
import { Pencil, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader, QueryState } from '../components/page';
import { useMarkSignupReviewed, useSignupAttempts, useUsers } from '../api/hooks';
import { DeleteUserDialog } from './DeleteUserDialog';
import { DeletePendingUserDialog } from './DeletePendingUserDialog';
import { DeleteSignupAttemptDialog } from './DeleteSignupAttemptDialog';
import type { AdminUser, PendingDirectoryUser, SignupAttempt } from '../api/types';
import {
  DIRECTORY_STATUSES,
  EMPTY_FILTERS,
  directoryRows,
  filterDirectory,
  reportsAccessRequests,
  reportsInternalUsers,
} from './user-directory';

const roleLabel = (role: string) =>
  role.charAt(0).toUpperCase() + role.slice(1).replaceAll('_', ' ');

function DirectoryFilter({
  label,
  value,
  onChange,
  options,
  allLabel = label === 'Status' ? 'All statuses' : `All ${label.toLowerCase()}`,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  allLabel?: string;
}) {
  return (
    <label className="flex min-w-36 flex-1 flex-col gap-1 text-xs text-muted-foreground">
      {label}
      <select
        aria-label={label}
        value={value}
        onChange={event => onChange(event.target.value)}
        className="h-10 w-full rounded-3xl border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <option value="">{allLabel}</option>
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function UsersPage() {
  const users = useUsers();
  const attempts = useSignupAttempts(null);
  const mark = useMarkSignupReviewed();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [deleteUser, setDeleteUser] = useState<AdminUser | null>(null);
  const [deletePending, setDeletePending] = useState<PendingDirectoryUser | null>(null);
  const [deleteAttempt, setDeleteAttempt] = useState<SignupAttempt | null>(null);
  const rows = useMemo(
    () => directoryRows(users.data, attempts.data?.attempts ?? []),
    [users.data, attempts.data]
  );
  const filtered = useMemo(() => filterDirectory(rows, filters), [rows, filters]);
  const tenants = [
    ...new Map(rows.filter(row => row.tenantId).map(row => [row.tenantId!, row.tenant!])).entries(),
  ]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const teams = [
    ...new Map(
      rows
        .filter(row => !filters.tenant || row.tenantId === filters.tenant)
        .flatMap(row => row.teams.map(team => [team.id, `${team.name} · ${row.tenant}`] as const))
    ).entries(),
  ]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const roles = [...new Set(rows.flatMap(row => row.roles))]
    .sort()
    .map(value => ({ value, label: roleLabel(value) }));
  const hasFilters = Object.values(filters).some(Boolean);
  const updateFilter = (field: keyof typeof filters, value: string) =>
    setFilters(previous => ({
      ...previous,
      [field]: value,
      ...(field === 'tenant' ? { team: '' } : {}),
    }));

  const markReviewed = async (id: string) => {
    try {
      await mark.mutateAsync(id);
      toast.success('Marked reviewed');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not mark this sign-up reviewed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Users and sign-ups"
        description="Registered users, pending team members, and rejected sign-ups across all tenants."
      />
      <div className="mb-4 space-y-3 rounded-3xl border bg-card p-4">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            aria-label="Search users and sign-ups"
            placeholder="Search users, email, tenants, teams, or roles…"
            value={filters.search}
            onChange={event => updateFilter('search', event.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <DirectoryFilter
            label="Tenants"
            value={filters.tenant}
            onChange={value => updateFilter('tenant', value)}
            options={[...tenants, { value: 'unassigned', label: 'No tenant' }]}
          />
          <DirectoryFilter
            label="Teams"
            value={filters.team}
            onChange={value => updateFilter('team', value)}
            options={[...teams, { value: 'unassigned', label: 'No team' }]}
          />
          <DirectoryFilter
            label="Roles"
            value={filters.role}
            onChange={value => updateFilter('role', value)}
            options={[...roles, { value: 'unassigned', label: 'No roles' }]}
          />
          <DirectoryFilter
            label="Status"
            value={filters.status}
            onChange={value => updateFilter('status', value)}
            options={DIRECTORY_STATUSES.map(value => ({ value, label: value }))}
          />
          {reportsAccessRequests(users.data) && (
            <DirectoryFilter
              label="Requests"
              value={filters.requests}
              onChange={value => updateFilter('requests', value)}
              options={[{ value: 'pending', label: 'PI access requested' }]}
            />
          )}
          {reportsInternalUsers(users.data) && (
            <DirectoryFilter
              label="Internal"
              allLabel="All users"
              value={filters.internal}
              onChange={value => updateFilter('internal', value)}
              options={[
                { value: 'internal', label: 'Internal only' },
                { value: 'external', label: 'Leave out internal' },
              ]}
            />
          )}
          {hasFilters && (
            <Button variant="ghost" onClick={() => setFilters(EMPTY_FILTERS)}>
              Clear filters
            </Button>
          )}
        </div>
      </div>
      {users.data && users.data.pending_users === undefined && (
        <p role="status" className="mb-3 text-sm text-muted-foreground">
          This server does not yet provide pending members or team details in this list.
        </p>
      )}
      <QueryState
        isLoading={users.isLoading || attempts.isLoading}
        error={users.error || attempts.error}
      >
        <p role="status" className="mb-3 text-sm text-muted-foreground">
          {filtered.length} of {rows.length} entries
        </p>
        <div className="overflow-hidden rounded-3xl border">
          <Table>
            <TableHeader>
              <TableRow>
                {['User', 'Email', 'Tenant', 'Team', 'Roles', 'Last login / status', 'Actions'].map(
                  label => (
                    <TableHead key={label}>{label}</TableHead>
                  )
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(row => (
                <TableRow key={row.key}>
                  <TableCell className="font-medium">
                    {row.user ? (
                      <Link
                        to="/users/$userId"
                        params={{ userId: row.user.id }}
                        className="hover:underline"
                      >
                        {row.name}
                      </Link>
                    ) : (
                      row.name
                    )}
                    {row.internal && (
                      <Badge
                        variant="outline"
                        className="ml-2"
                        title="Left out of Product health statistics"
                      >
                        Internal
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{row.email || 'No email'}</TableCell>
                  <TableCell>
                    {row.tenantId ? (
                      <Link
                        to="/tenants/$tenantId"
                        params={{ tenantId: row.tenantId }}
                        className="hover:underline"
                      >
                        {row.tenant}
                      </Link>
                    ) : (
                      'No tenant'
                    )}
                  </TableCell>
                  <TableCell>
                    {row.teams.length
                      ? row.teams.map(team => (
                          <div key={team.id}>
                            <Link
                              to="/tenants/$tenantId/teams/$teamId"
                              params={{ tenantId: row.tenantId!, teamId: team.id }}
                              className="hover:underline"
                            >
                              {team.name}
                            </Link>
                          </div>
                        ))
                      : 'No team'}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {row.roles.length
                        ? row.roles.map(role => (
                            <Badge key={role} variant="secondary">
                              {roleLabel(role)}
                            </Badge>
                          ))
                        : 'No roles'}
                    </div>
                  </TableCell>
                  <TableCell className="min-w-44">
                    {row.lastLogin ? (
                      <time dateTime={row.lastLogin}>
                        {new Date(row.lastLogin).toLocaleString()}
                      </time>
                    ) : (
                      <Badge
                        variant={
                          row.status === 'Rejected' || row.status === 'Needs attention'
                            ? 'destructive'
                            : 'outline'
                        }
                      >
                        {row.status}
                      </Badge>
                    )}
                    {row.detail && (
                      <p className="mt-1 max-w-xs text-xs text-muted-foreground">{row.detail}</p>
                    )}
                    {row.attemptedAt && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Attempted {new Date(row.attemptedAt).toLocaleString()}
                      </p>
                    )}
                    {row.user &&
                      row.accessRequests.map(request => (
                        <Link
                          key={request.id}
                          to="/users/$userId"
                          params={{ userId: row.user!.id }}
                          className="mt-1 block w-fit"
                        >
                          <Badge variant="default">
                            Access request · {request.pi_name ?? request.pi_slug}
                          </Badge>
                        </Link>
                      ))}
                  </TableCell>
                  <TableCell>
                    {row.user && (
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" asChild>
                          <Link
                            to="/users/$userId"
                            params={{ userId: row.user.id }}
                            aria-label={`Edit ${row.name}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          aria-label={`Delete ${row.name}`}
                          onClick={() => setDeleteUser(row.user!)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                    {row.pending && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        aria-label={`Delete pending sign-up ${row.email}`}
                        onClick={() => setDeletePending(row.pending!)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                    {row.attempt && (
                      <div className="flex items-center gap-1">
                        {!row.attempt.reviewed_at && (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={mark.isPending}
                            onClick={() => void markReviewed(row.attempt!.id)}
                          >
                            Mark reviewed
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          aria-label={`Delete sign-up attempt ${row.email}`}
                          onClick={() => setDeleteAttempt(row.attempt!)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="h-28 text-center text-muted-foreground">
                    {hasFilters
                      ? 'No users or sign-ups match these filters.'
                      : 'No users or sign-ups yet.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </QueryState>
      <DeleteUserDialog
        user={deleteUser}
        open={deleteUser !== null}
        onClose={() => setDeleteUser(null)}
      />
      <DeletePendingUserDialog person={deletePending} onClose={() => setDeletePending(null)} />
      <DeleteSignupAttemptDialog attempt={deleteAttempt} onClose={() => setDeleteAttempt(null)} />
    </div>
  );
}
